
import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import ProfileAvatar from "./ProfileAvatar";
import { Upload } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useImageUpload } from "@/hooks/useImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImageUpload } from "@/components/ui/image-upload";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface ProfileHeaderProps {
  fullName: string;
  username: string;
  avatarUrl?: string;
  country?: string;
  state?: string;
  school: string;
  gradeLevel: string;
}

const ProfileHeader = ({ 
  fullName, 
  username, 
  avatarUrl, 
  country,
  state,
  school,
  gradeLevel
}: ProfileHeaderProps) => {
  const [showUploadDialog, setShowUploadDialog] = React.useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const { imageUrl, uploading, handleUpload } = useImageUpload({
    initialUrl: avatarUrl,
  });
  
  const handleImageUpload = async (url: string) => {
    if (!user) return;
    
    try {
      // Update the profile in Supabase
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: url })
        .eq('id', user.id);
      
      if (error) throw error;
      
      toast({
        title: "Profile updated",
        description: "Your profile picture has been updated successfully",
      });
      
      // Close the dialog
      setShowUploadDialog(false);
      
      // Refresh the page to show the new avatar
      window.location.reload();
    } catch (error) {
      console.error("Error updating profile picture:", error);
      toast({
        title: "Error",
        description: "Failed to update profile picture",
        variant: "destructive"
      });
    }
  };

  return (
    <motion.div 
      className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative">
          <ProfileAvatar avatarUrl={avatarUrl} fullName={fullName} />
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  className="absolute bottom-0 right-0 rounded-full h-8 w-8 bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => setShowUploadDialog(true)}
                >
                  <Upload className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Upload profile picture</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        <div className="text-center sm:text-left">
          <h2 className="text-xl font-bold">{fullName}</h2>
          <p className="text-muted-foreground">@{username}</p>
          
          {(country || state) && (
            <p className="text-sm text-muted-foreground mt-1">
              {[state, country].filter(Boolean).join(", ")}
            </p>
          )}
          
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1">
            {school && (
              <span className="text-sm">
                <span className="font-medium">School:</span> {school}
              </span>
            )}
            
            {gradeLevel && (
              <span className="text-sm">
                <span className="font-medium">Grade:</span> {gradeLevel}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Profile Photo Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Profile Picture</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            <ImageUpload
              value={imageUrl || avatarUrl}
              onChange={handleImageUpload}
              disabled={uploading}
              size="lg"
            />
            <p className="mt-4 text-sm text-muted-foreground text-center">
              Upload a profile picture to personalize your account.
              <br />
              Square images work best.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default ProfileHeader;
